import type React from 'react';

import { useTheme, Box } from '@mui/material';
import { useNodeId, useStore } from '@xyflow/react';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import { useNodeColor } from '../hooks/useNodeColor';

import type { Node } from '@xyflow/react';

export const prepareUpdateData = (nodes: Node[], nodeId: string | null, nodeLabel: string): Node[] | false => {
  flowchartLogger.debug('NodeWrapper: updateNodeLabel', { nodeId, nodeLabel });
  if (nodes === null || nodes === undefined) {
    flowchartLogger.debug('NodeWrapper: nodes is null or undefined. Update cancelled.');
    return false;
  }
  const newNodes = nodes.map((node) => {
    if (node.id === nodeId) {
      flowchartLogger.debug('NodeWrapper: updating node label', { nodeId, nodeLabel });
      return {
        ...node,
        data: {
          ...node.data,
          label: nodeLabel,
        },
      };
    }
    return node;
  });

  return newNodes;
};

interface NodeWrapperProps {
  children?: React.ReactNode | null;
  style?: React.CSSProperties;
  condensed?: boolean;
  errors?: string[];
}

const nodeWidth = '200px';

// This component is used to provide common styling and functionality to all custom nodes.
const NodeWrapper = ({
  children = null,
  style = {},
  condensed = false,
  errors = [],
}: NodeWrapperProps): React.ReactElement => {
  const nodeId = useNodeId();
  const theme = useTheme();
  const translate = useTranslate();

  // Use store selector to subscribe to node changes (triggers re-render on selection)
  const currentNode = useStore((state) => state.nodeLookup.get(nodeId ?? ''));
  const backgroundColor = useNodeColor(currentNode);
  const serverErrors =
    currentNode?.data !== undefined &&
    currentNode.data !== null &&
    Array.isArray((currentNode.data as { serverErrors?: unknown }).serverErrors)
      ? ((currentNode.data as { serverErrors?: string[] }).serverErrors ?? [])
      : [];
  const allErrors = [...errors, ...serverErrors];

  return (
    <>
      <Box
        sx={{
          borderRadius: 1,
          backgroundColor,
          color: theme.palette.getContrastText(backgroundColor),
          borderWidth: '2px',
          borderStyle: 'solid',
          borderColor: currentNode?.selected === true ? '#000' : 'transparent',
          boxShadow: allErrors.length > 0 ? `${theme.palette.error.main} 0px 0px 0px 2px` : undefined,
          overflow: 'hidden',
          width: !condensed ? nodeWidth : undefined,
          '&:hover': {
            '.node-controls-wrapper': {
              visibility: 'visible',
            },
          },
          ...style,
        }}
        {...(currentNode !== undefined && 'type' in currentNode && currentNode.type !== undefined
          ? { 'data-testid': `${currentNode.type}-node` }
          : {})}
      >
        {children}
      </Box>
      {allErrors.length > 0 && (
        <Box
          sx={{
            color: theme.palette.error.main,
            fontSize: '0.63rem',
            lineHeight: 'normal',
            mt: 0.5,
            pl: 1,
            height: 0,
            width: nodeWidth,
          }}
        >
          {allErrors.map((error) => (
            <Box data-testid='node-error' key={error} sx={{ whiteSpace: 'nowrap' }}>
              {translate(error, { _: error })}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export default NodeWrapper;
